import React from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import AppleIcon from "@mui/icons-material/Apple";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import ShopIcon from "@mui/icons-material/Shop";
import WindowIcon from "@mui/icons-material/Window";
import { motion } from "framer-motion";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import {
  authBackgroundContainerSx,
  authOrb1Sx,
  authOrb2Sx
} from "../common/AuthSidePanel/authBackgroundStyles";
import Topbar from "../Navigation/Topbar";
import WelcomeFooter from "../Welcome/WelcomeFooter";
import {
  isEmbeddedShellActive,
  markFirstVisitPopupDownloadClicked
} from "../../Utils/embeddedShellSession";
import {
  type DesktopInstallerFileDto,
  useDesktopInstallers
} from "../../hooks/api/useDesktopInstallers";

const downloadHref = (url: string | undefined): string | undefined => {
  const u = url?.trim();
  return u || undefined;
};

const desktopDownloadLinkSx = (theme: Theme) => ({
  fontWeight: 400,
  fontSize: "1rem",
  color: theme.palette.primary.main,
  textUnderlineOffset: "3px",
  transition: "color 0.2s ease, text-decoration-color 0.2s ease",
  "&:hover": {
    color: theme.palette.primary.dark
  }
});

const macDownloadLabel = (
  file: DesktopInstallerFileDto,
  t: (key: string, fallback: string) => string
): string => {
  const v = (file.variant ?? "").toLowerCase();
  if (v === "dmg") {
    return t("download-apps-download-dmg", "Download for Mac (.dmg)");
  }
  if (v === "zip") {
    return t("download-apps-download-zip", "Download for Mac (.zip)");
  }
  return t("download-apps-download-mac-file", "Download for Mac");
};

const glassSurface = {
  borderRadius: { xs: 2, sm: 3 },
  backgroundColor: alpha("#fff", 0.94),
  backdropFilter: "saturate(180%) blur(14px)",
  WebkitBackdropFilter: "saturate(180%) blur(14px)",
  border: `1px solid ${alpha("#fff", 0.65)}`,
  boxShadow: `0 12px 40px ${alpha("#1a0a3e", 0.18)}`
};

const fadeUpContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.09, delayChildren: 0.06 }
  }
};

const fadeUpItem = {
  hidden: { opacity: 0, y: 18 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] as const }
  }
};

const MotionBox = motion(Box);

const StepBlock = ({ step, text }: { step: number; text: string }) => (
  <Stack direction="row" spacing={2} alignItems="flex-start">
    <Box
      sx={{
        flexShrink: 0,
        width: 40,
        height: 40,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "white",
        fontWeight: 700,
        fontSize: "0.95rem",
        boxShadow: `0 4px 14px ${alpha("#4c2a85", 0.45)}`
      }}
    >
      {step}
    </Box>
    <Typography
      variant="body2"
      sx={{
        color: "text.primary",
        lineHeight: 1.65,
        pt: 0.75,
        flex: 1
      }}
    >
      {text}
    </Typography>
  </Stack>
);

const DownloadAppsPage: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const theme = useTheme();
  const isMobileDevice = useMediaQuery(theme.breakpoints.down("md"), {
    noSsr: true
  });
  const hideAppDownloads = isEmbeddedShellActive();

  const onPersistFirstVisitDownloadIntent = () => {
    markFirstVisitPopupDownloadClicked();
  };

  const { data: desktopPayload, isPending: desktopLoading } =
    useDesktopInstallers(!hideAppDownloads);

  const latest = desktopPayload?.revisions?.[0];
  const apiWinUrl = downloadHref(latest?.windows?.downloadUrl);
  const apiMacFiles = latest?.mac?.length ? latest.mac : [];

  const winUrl = apiWinUrl;

  const macPrimaryFromApi =
    apiMacFiles.find((m) => (m.variant ?? "").toLowerCase() === "dmg") ??
    apiMacFiles[0];
  const macUrl = downloadHref(macPrimaryFromApi?.downloadUrl);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh"
      }}
    >
      <Topbar
        isMembership
        showMembershipSubNav={false}
        openFlyOver={() => undefined}
        showMenuIcon={false}
      />
      <Box
        sx={{
          ...authBackgroundContainerSx,
          flex: 1,
          minHeight: 0,
          py: { xs: 3, sm: 5 },
          display: "flex",
          flexDirection: "column"
        }}
      >
        <Box sx={authOrb1Sx} />
        <Box sx={authOrb2Sx} />

        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <MotionBox variants={fadeUpContainer} initial="hidden" animate="show">
            <Stack spacing={{ xs: 3, sm: 4 }}>
              <MotionBox variants={fadeUpItem}>
                <Stack spacing={1.5} alignItems="center" textAlign="center">
                  <Typography
                    variant="h3"
                    component="h1"
                    fontWeight={800}
                    color="white"
                    sx={{
                      fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.5rem" },
                      lineHeight: 1.2,
                      textAlign: "center",
                      textShadow: "0 2px 24px rgba(0,0,0,0.15)",
                      maxWidth: 720,
                      px: { xs: 1, sm: 0 }
                    }}
                  >
                    {hideAppDownloads
                      ? t(
                          "download-apps-page-title-embedded",
                          "You're using Kivunova FMS in the installed app"
                        )
                      : isMobileDevice
                        ? t(
                            "download-apps-page-title-mobile",
                            "Get Kivunova FMS on your phone"
                          )
                        : t(
                            "download-apps-page-title-desktop",
                            "Download Kivunova FMS on your computer"
                          )}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: alpha("#fff", 0.92),
                      maxWidth: 560,
                      mx: "auto",
                      fontSize: { xs: "1rem", sm: "1.05rem" },
                      lineHeight: 1.65,
                      textAlign: "center",
                      px: { xs: 1, sm: 0 }
                    }}
                  >
                    {hideAppDownloads
                      ? t(
                          "download-apps-page-intro-embedded",
                          "App downloads are not shown here because this build is already running inside the desktop or mobile application."
                        )
                      : isMobileDevice
                        ? t(
                            "download-apps-page-intro-mobile",
                            "Install the app and sign in with the same account you use in the browser — optimized for small screens."
                          )
                        : t(
                            "download-apps-page-intro-desktop",
                            "Download the desktop app for a focused window and system shortcuts. Your data and sign-in stay the same as on the web."
                          )}
                  </Typography>
                </Stack>
              </MotionBox>

              {!hideAppDownloads && (
                <MotionBox variants={fadeUpItem}>
                  <Card
                    elevation={0}
                    sx={{ ...glassSurface, overflow: "visible" }}
                  >
                    <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                      <Typography
                        variant="overline"
                        sx={{
                          letterSpacing: "0.12em",
                          fontWeight: 700,
                          color: "primary.main",
                          display: "block",
                          mb: 2
                        }}
                      >
                        {t("download-apps-sop-heading", "How it works")}
                      </Typography>
                      <Stack spacing={2.5}>
                        <StepBlock
                          step={1}
                          text={t(
                            isMobileDevice
                              ? "download-apps-sop-1-mobile"
                              : "download-apps-sop-1-desktop",
                            isMobileDevice
                              ? "Choose App Store or Google Play below."
                              : "Choose Windows or Mac below."
                          )}
                        />
                        <StepBlock
                          step={2}
                          text={t(
                            "download-apps-sop-2",
                            "Download the installer or open the store page, then complete installation."
                          )}
                        />
                        <StepBlock
                          step={3}
                          text={t(
                            "download-apps-sop-3",
                            "Open the app — it loads the same Kivunova FMS experience you know from the browser."
                          )}
                        />
                        <StepBlock
                          step={4}
                          text={t(
                            "download-apps-sop-4",
                            "Sign in with your organization credentials. If anything fails, try the browser first to verify your account."
                          )}
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                </MotionBox>
              )}

              {!hideAppDownloads && !isMobileDevice && (
                <MotionBox variants={fadeUpItem}>
                  <Stack spacing={2}>
                    <Typography
                      variant="overline"
                      sx={{
                        letterSpacing: "0.1em",
                        fontWeight: 700,
                        color: alpha("#fff", 0.95)
                      }}
                    >
                      {t("download-apps-desktop-heading", "Desktop")}
                    </Typography>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2.5}
                    >
                      <motion.div
                        style={{ flex: 1 }}
                        whileHover={{
                          y: -6,
                          transition: {
                            type: "spring",
                            stiffness: 400,
                            damping: 24
                          }
                        }}
                      >
                        <Card
                          elevation={0}
                          sx={{
                            ...glassSurface,
                            height: "100%",
                            transition: "box-shadow 0.25s ease",
                            "&:hover": {
                              boxShadow: `0 20px 48px ${alpha("#1a0a3e", 0.22)}`
                            }
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Stack spacing={2} alignItems="flex-start">
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1.5}
                              >
                                <Box
                                  sx={{
                                    p: 1.25,
                                    borderRadius: 2,
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.12
                                    )
                                  }}
                                >
                                  <WindowIcon
                                    color="primary"
                                    sx={{ fontSize: 32 }}
                                  />
                                </Box>
                                <Typography variant="h6" fontWeight={700}>
                                  {t("download-apps-windows-label", "Windows")}
                                </Typography>
                              </Stack>
                              {desktopLoading && !winUrl ? (
                                <Stack alignItems="center" py={1}>
                                  <CircularProgress size={28} />
                                </Stack>
                              ) : winUrl ? (
                                <Link
                                  href={winUrl}
                                  rel="noopener noreferrer"
                                  underline="always"
                                  data-testid="download-apps-windows-link"
                                  onClick={onPersistFirstVisitDownloadIntent}
                                  sx={{
                                    alignSelf: "flex-start",
                                    ...desktopDownloadLinkSx(theme)
                                  }}
                                >
                                  {t(
                                    "download-apps-download-exe",
                                    "Download for Windows"
                                  )}
                                </Link>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {t(
                                    "download-apps-url-pending",
                                    "Installer link coming soon. Until then, you can use Kivunova FMS in Chrome or Edge — same features, no install required."
                                  )}
                                </Typography>
                              )}
                            </Stack>
                          </CardContent>
                        </Card>
                      </motion.div>
                      <motion.div
                        style={{ flex: 1 }}
                        whileHover={{
                          y: -6,
                          transition: {
                            type: "spring",
                            stiffness: 400,
                            damping: 24
                          }
                        }}
                      >
                        <Card
                          elevation={0}
                          sx={{
                            ...glassSurface,
                            height: "100%",
                            transition: "box-shadow 0.25s ease",
                            "&:hover": {
                              boxShadow: `0 20px 48px ${alpha("#1a0a3e", 0.22)}`
                            }
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Stack spacing={2} alignItems="flex-start">
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1.5}
                              >
                                <Box
                                  sx={{
                                    p: 1.25,
                                    borderRadius: 2,
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.12
                                    )
                                  }}
                                >
                                  <AppleIcon
                                    color="primary"
                                    sx={{ fontSize: 32 }}
                                  />
                                </Box>
                                <Typography variant="h6" fontWeight={700}>
                                  {t("download-apps-mac-label", "Macbook")}
                                </Typography>
                              </Stack>
                              {desktopLoading &&
                              apiMacFiles.length === 0 &&
                              !macUrl ? (
                                <Stack alignItems="center" py={1}>
                                  <CircularProgress size={28} />
                                </Stack>
                              ) : apiMacFiles.length > 0 ? (
                                <Stack
                                  spacing={1.25}
                                  width="100%"
                                  alignItems="flex-start"
                                >
                                  {apiMacFiles.map((file) => (
                                    <Link
                                      key={file.key}
                                      href={file.downloadUrl}
                                      rel="noopener noreferrer"
                                      underline="always"
                                      data-testid="download-apps-mac-link"
                                      onClick={
                                        onPersistFirstVisitDownloadIntent
                                      }
                                      sx={desktopDownloadLinkSx(theme)}
                                    >
                                      {macDownloadLabel(file, t)}
                                    </Link>
                                  ))}
                                </Stack>
                              ) : macUrl ? (
                                <Link
                                  href={macUrl}
                                  rel="noopener noreferrer"
                                  underline="always"
                                  data-testid="download-apps-mac-link"
                                  onClick={onPersistFirstVisitDownloadIntent}
                                  sx={{
                                    alignSelf: "flex-start",
                                    ...desktopDownloadLinkSx(theme)
                                  }}
                                >
                                  {t(
                                    "download-apps-download-dmg",
                                    "Download for Mac"
                                  )}
                                </Link>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {t(
                                    "download-apps-mac-url-pending",
                                    "Mac download coming soon. You can keep using Kivunova FMS in Safari or Chrome until it’s available."
                                  )}
                                </Typography>
                              )}
                            </Stack>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Stack>
                  </Stack>
                </MotionBox>
              )}

              {!hideAppDownloads && isMobileDevice && (
                <MotionBox variants={fadeUpItem}>
                  <Stack spacing={2}>
                    <Typography
                      variant="overline"
                      sx={{
                        letterSpacing: "0.1em",
                        fontWeight: 700,
                        color: alpha("#fff", 0.95)
                      }}
                    >
                      {t("download-apps-mobile-heading", "Mobile")}
                    </Typography>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2.5}
                    >
                      <motion.div
                        style={{ flex: 1 }}
                        whileHover={{
                          y: -6,
                          transition: {
                            type: "spring",
                            stiffness: 400,
                            damping: 24
                          }
                        }}
                      >
                        <Card
                          elevation={0}
                          data-testid="download-apps-ios-card"
                          sx={{
                            ...glassSurface,
                            height: "100%",
                            "&:hover": {
                              boxShadow: `0 20px 48px ${alpha("#1a0a3e", 0.22)}`
                            }
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Stack spacing={2} alignItems="flex-start">
                              <Box
                                sx={{
                                  p: 1.25,
                                  borderRadius: 2,
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.12
                                  )
                                }}
                              >
                                <PhoneIphoneIcon
                                  color="primary"
                                  sx={{ fontSize: 32 }}
                                />
                              </Box>
                              <Typography variant="h6" fontWeight={700}>
                                {t("download-apps-ios-label", "iPhone & iPad")}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {t(
                                  "download-apps-ios-pending",
                                  "App Store link coming soon. You can add Kivunova FMS to your Home Screen from Safari in the meantime."
                                )}
                              </Typography>
                            </Stack>
                          </CardContent>
                        </Card>
                      </motion.div>
                      <motion.div
                        style={{ flex: 1 }}
                        whileHover={{
                          y: -6,
                          transition: {
                            type: "spring",
                            stiffness: 400,
                            damping: 24
                          }
                        }}
                      >
                        <Card
                          elevation={0}
                          data-testid="download-apps-android-card"
                          sx={{
                            ...glassSurface,
                            height: "100%",
                            "&:hover": {
                              boxShadow: `0 20px 48px ${alpha("#1a0a3e", 0.22)}`
                            }
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Stack spacing={2} alignItems="flex-start">
                              <Box
                                sx={{
                                  p: 1.25,
                                  borderRadius: 2,
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.12
                                  )
                                }}
                              >
                                <ShopIcon
                                  color="primary"
                                  sx={{ fontSize: 32 }}
                                />
                              </Box>
                              <Typography variant="h6" fontWeight={700}>
                                {t("download-apps-android-label", "Android")}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {t(
                                  "download-apps-android-pending",
                                  "Google Play link coming soon. Use Kivunova FMS in Chrome on your phone until the app is listed."
                                )}
                              </Typography>
                            </Stack>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Stack>
                  </Stack>
                </MotionBox>
              )}
            </Stack>
          </MotionBox>
        </Container>
      </Box>
      <WelcomeFooter />
    </Box>
  );
};

export default DownloadAppsPage;
